import React from 'react';
import Badge from './Badge';
import BadgeLevelModal from './BadgeLevelModal';
import { BadgeTotemIcon } from './BadgeTotem';
import { formatBadgePercentage, roundedBadgePercentageValue } from './badgePercentageFormatting';

const complianceLevelConfigs = {
  week: {
    title: 'Weekly Flame Levels',
    description: 'Weekly consistency fuels the flame outline around your badge.',
    levels: [
      {
        label: 'Embers',
        icon: <span role="img" aria-label="embers flame" style={{ filter: 'grayscale(100%) opacity(0.5)' }}>🔥</span>,
        threshold: 0,
        thresholdLabel: '< 30%',
        description: 'Keep logging workouts to light the flame.'
      },
      {
        label: 'Red Flame',
        icon: <span role="img" aria-label="red flame" style={{ filter: 'hue-rotate(-20deg) saturate(3)' }}>🔥</span>,
        threshold: 30,
        thresholdLabel: '>= 30%',
        description: 'The spark has caught. Keep feeding the fire.'
      },
      {
        label: 'Orange Flame',
        icon: <span role="img" aria-label="orange flame">🔥</span>,
        threshold: 60,
        thresholdLabel: '>= 60%',
        description: 'Sustained effort ignites the orange flame ring.'
      },
      {
        label: 'White Flame',
        icon: <span role="img" aria-label="white flame" style={{ filter: 'grayscale(100%) brightness(3)' }}>🔥</span>,
        threshold: 90,
        thresholdLabel: '>= 90%',
        description: 'Intense heat. Your consistency is burning bright.'
      },
      {
        label: 'Platinum Flame',
        icon: <span role="img" aria-label="platinum flame" style={{ filter: 'grayscale(100%) brightness(2.4) contrast(1.25)' }}>🔥</span>,
        threshold: 100,
        thresholdLabel: '100%',
        description: 'Perfect weekly commitment burns white-hot with a restrained platinum halo.'
      }
    ]
  },
  month: {
    title: 'Monthly Aura Levels',
    description: 'Monthly consistency unlocks shimmering effects.',
    levels: [
      {
        label: 'No Shimmer',
        icon: <span className="badge-shimmer-preview badge-shimmer-preview-none" aria-hidden="true" />,
        threshold: 0,
        thresholdLabel: '< 30%',
        description: 'Rack up more completions to unlock the shimmer.'
      },
      {
        label: 'Bronze Shimmer',
        icon: <span className="badge-shimmer-preview shimmer shimmer-bronze shimmer-overlay" aria-hidden="true" />,
        threshold: 30,
        thresholdLabel: '>= 30%',
        description: 'Bronze shimmer shows solid monthly focus.'
      },
      {
        label: 'Silver Shimmer',
        icon: <span className="badge-shimmer-preview shimmer shimmer-silver shimmer-overlay" aria-hidden="true" />,
        threshold: 60,
        thresholdLabel: '>= 60%',
        description: 'Silver shimmer reflects consistent dedication.'
      },
      {
        label: 'Gold Shimmer',
        icon: <span className="badge-shimmer-preview shimmer shimmer-gold shimmer-overlay" aria-hidden="true" />,
        threshold: 90,
        thresholdLabel: '>= 90%',
        description: 'Gold shimmer signifies elite monthly performance.'
      },
      {
        label: 'Obsidian Finish',
        icon: <span className="badge-shimmer-preview shimmer shimmer-obsidian shimmer-overlay" aria-hidden="true" />,
        threshold: 100,
        thresholdLabel: '100%',
        description: 'Black nickel, platinum highlights, and a restrained gold seam mark flawless monthly execution.'
      }
    ]
  },
  year: {
    title: 'Yearly Totem Levels',
    description: 'Year-long consistency changes the creature guarding the center of the badge.',
    levels: [
      { label: 'Potato', icon: <BadgeTotemIcon totemKey="potato" label="Potato totem" />, threshold: 0, thresholdLabel: '>= 0.0%', description: 'Every journey starts underground. You’ve planted the seed — showing up is how progress begins.' },
      { label: 'Mouse', icon: <BadgeTotemIcon totemKey="mouse" label="Mouse totem" />, threshold: 8, thresholdLabel: '>= 8.0%', description: 'Small steps create real movement. The habit is beginning to form.' },
      { label: 'Raccoon', icon: <BadgeTotemIcon totemKey="raccoon" label="Raccoon totem" />, threshold: 17, thresholdLabel: '>= 17.0%', description: 'Adaptable and engaged. You’re learning how training fits into your life.' },
      { label: 'Cat', icon: <BadgeTotemIcon totemKey="cat" label="Cat totem" />, threshold: 25, thresholdLabel: '>= 25.0%', description: 'Finding your rhythm. The routine is becoming familiar and comfortable.' },
      { label: 'Dog', icon: <BadgeTotemIcon totemKey="dog" label="Dog totem" />, threshold: 33, thresholdLabel: '>= 33.0%', description: 'Training with intent. Workouts are now part of your regular week.' },
      { label: 'Fox', icon: <BadgeTotemIcon totemKey="fox" label="Fox totem" />, threshold: 42, thresholdLabel: '>= 42.0%', description: 'Smart and consistent. You’re training with awareness and intention.' },
      { label: 'Donkey', icon: <BadgeTotemIcon totemKey="donkey" label="Donkey totem" />, threshold: 50, thresholdLabel: '>= 50.0%', description: 'Steady and reliable. Progress comes from showing up — and you do.' },
      { label: 'Horse', icon: <BadgeTotemIcon totemKey="horse" label="Horse totem" />, threshold: 58, thresholdLabel: '>= 58.0%', description: 'Strength in motion. Endurance and consistency are clearly growing.' },
      { label: 'Wolf', icon: <BadgeTotemIcon totemKey="wolf" label="Wolf totem" />, threshold: 67, thresholdLabel: '>= 67.0%', description: 'Disciplined and focused. Your routine is structured and dependable.' },
      { label: 'Gorilla', icon: <BadgeTotemIcon totemKey="gorilla" label="Gorilla totem" />, threshold: 75, thresholdLabel: '>= 75.0%', description: 'Trained power. Strength, control, and resilience are now unmistakable.' },
      { label: 'Lion', icon: <BadgeTotemIcon totemKey="lion" label="Lion totem" />, threshold: 83, thresholdLabel: '>= 83.0%', description: 'Confidence and mastery. Training is deliberate, consistent, and strong.' },
      { label: 'Eagle', icon: <BadgeTotemIcon totemKey="eagle" label="Eagle totem" />, threshold: 92, thresholdLabel: '>= 92.0%', description: 'Clarity and command. You train with perspective, control, and purpose.' },
      { label: 'Dragon', icon: <BadgeTotemIcon totemKey="dragon" label="Dragon totem" />, threshold: 100, thresholdLabel: '100.0%', description: 'Legendary consistency. Twelve months. No breaks. Rare and extraordinary.' }
    ]
  },
  sevenYears: {
    title: 'Seven-Year Starfield',
    description: 'Each star marks another gold year (≥ 90% consistency). Earn your first after sustaining two gold years.',
    valueLabel: 'Stars Earned',
    formatValue: (value) => `${value} ${value === 1 ? 'star' : 'stars'}`,
    secondaryValueLabel: 'Gold Years Logged',
    formatSecondaryValue: (value) => `${value.toFixed(1)} yrs`,
    getCurrentValue: ({ sevenYearStars }) => sevenYearStars,
    getSecondaryValue: ({ goldYearEquivalents }) => goldYearEquivalents,
    levels: [
      { label: 'Galactic Vanguard', icon: <span role="img" aria-label="galactic vanguard">🌌</span>, threshold: 9, thresholdLabel: '9+ stars (10+ gold years)', description: 'A legendary halo surrounds your badge.' },
      { label: 'Supercluster', icon: <span role="img" aria-label="supercluster">💫</span>, threshold: 6, thresholdLabel: '6–8 stars (7–9 gold years)', description: 'A sprawling supercluster of victories.' },
      { label: 'Constellation', icon: <span role="img" aria-label="constellation">✨</span>, threshold: 4, thresholdLabel: '4–5 stars (5–6 gold years)', description: 'A bright constellation forms on the border.' },
      { label: 'Binary Star', icon: <span role="img" aria-label="binary star">⚝</span>, threshold: 1, thresholdLabel: '1–3 stars (2–4 gold years)', description: 'The first long-term stars begin to orbit.' },
      { label: 'Spark', icon: <span role="img" aria-label="spark">⋆</span>, threshold: 0, thresholdLabel: '< 1 star (< 2 gold years)', description: 'Stay gold for two full years to unlock your first star.' }
    ]
  },
  // Volume PR configs for each time horizon
  growthWeek: {
    title: 'Weekly Growth — Animation Speed',
    description: 'Your weekly PR rate controls how fast and how many arrows animate behind your badge.',
    valueLabel: 'Week PR Rate',
    formatValue: formatBadgePercentage,
    getCurrentValue: ({ volumePRs }) => volumePRs?.week?.prRate || 0,
    levels: [
      { label: 'Slow Drift', icon: <span className="arrow-speed-preview arrow-speed-dim" style={{ '--arrow-speed': '2.5s' }}><span className="arrow-speed-arrow" style={{ animationDelay: '0s' }}>▲</span></span>, threshold: 0, thresholdLabel: '< 30%', description: '2 arrows, 5s cycle — room to grow.' },
      { label: 'Gentle Lift', icon: <span className="arrow-speed-preview" style={{ '--arrow-speed': '1.8s' }}><span className="arrow-speed-arrow" style={{ animationDelay: '0s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.35s' }}>▲</span></span>, threshold: 30, thresholdLabel: '>= 30%', description: '4 arrows, 3.5s cycle — building momentum.' },
      { label: 'Steady Rise', icon: <span className="arrow-speed-preview" style={{ '--arrow-speed': '1.3s' }}><span className="arrow-speed-arrow" style={{ animationDelay: '0s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.25s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.5s' }}>▲</span></span>, threshold: 60, thresholdLabel: '>= 60%', description: '7 arrows, 2.5s cycle — consistent improvement.' },
      { label: 'Fast Flow', icon: <span className="arrow-speed-preview" style={{ '--arrow-speed': '1s' }}><span className="arrow-speed-arrow" style={{ animationDelay: '0s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.2s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.4s' }}>▲</span></span>, threshold: 90, thresholdLabel: '>= 90%', description: '10 arrows, 2s cycle — rapid weekly gains.' },
      { label: '⭐ Hyperdrive', icon: <span className="arrow-speed-preview arrow-speed-elite" style={{ '--arrow-speed': '0.5s' }}><span className="arrow-speed-arrow" style={{ animationDelay: '0s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.1s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.2s' }}>▲</span><span className="arrow-speed-arrow" style={{ animationDelay: '0.3s' }}>▲</span></span>, threshold: 95, thresholdLabel: '>= 95%', description: 'LEGENDARY! A blazing torrent of arrows — 15 arrows at 1s cycle. The ultimate display of weekly dominance.' }
    ]
  },
  growthMonth: {
    title: 'Monthly Growth — Arrow Size',
    description: 'Your monthly PR rate determines the size and style of the arrows.',
    valueLabel: 'Month PR Rate',
    formatValue: formatBadgePercentage,
    getCurrentValue: ({ volumePRs }) => volumePRs?.month?.prRate || 0,
    levels: [
      { label: 'Chevrons', icon: <span style={{ fontSize: '10px', opacity: 0.6 }}>˄</span>, threshold: 0, thresholdLabel: '< 30%', description: 'Tiny chevrons — keep pushing for more PRs.' },
      { label: 'Small Outline', icon: <span style={{ fontSize: '12px' }}>△</span>, threshold: 30, thresholdLabel: '>= 30%', description: 'Small outline arrows — building momentum.' },
      { label: 'Medium Outline', icon: <span style={{ fontSize: '14px' }}>△</span>, threshold: 60, thresholdLabel: '>= 60%', description: 'Outline triangles for good monthly performance.' },
      { label: 'Large Filled', icon: <span style={{ fontSize: '16px' }}>▲</span>, threshold: 90, thresholdLabel: '>= 90%', description: 'Solid filled arrows show strong monthly progress.' },
      { label: '⭐ Colossal', icon: <span className="growth-elite-icon" style={{ fontSize: '22px', textShadow: '0 0 8px currentColor' }}>▲</span>, threshold: 95, thresholdLabel: '>= 95%', description: 'LEGENDARY! Massive glowing arrows dominate the background — an exceptional month of progressive overload.' }
    ]
  },
  growthYear: {
    title: 'Yearly Growth — Arrow Color',
    description: 'Your yearly PR rate determines the color of the animated arrows rising behind your badge.',
    valueLabel: 'Year PR Rate',
    formatValue: formatBadgePercentage,
    getCurrentValue: ({ volumePRs }) => volumePRs?.year?.prRate || 0,
    levels: [
      { label: 'Grey Arrows', icon: <span style={{ opacity: 0.4 }}>▲</span>, threshold: 0, thresholdLabel: '< 30%', description: 'Colorless arrows — maintain your PRs over time to unlock colors.' },
      { label: 'Blue Arrows', icon: <span style={{ color: '#60a5fa' }}>▲</span>, threshold: 30, thresholdLabel: '>= 30%', description: 'Blue arrows mark solid yearly progress.' },
      { label: 'Lime Arrows', icon: <span style={{ color: '#a3e635' }}>▲</span>, threshold: 60, thresholdLabel: '>= 60%', description: 'Lime arrows — strong yearly gains.' },
      { label: 'Gold Arrows', icon: <span style={{ color: '#fbbf24' }}>▲</span>, threshold: 90, thresholdLabel: '>= 90%', description: 'Golden arrows show exceptional yearly consistency.' },
      { label: 'Platinum Arrows', icon: <span className="growth-elite-icon platinum-glow" style={{ color: '#f8fafc' }}>▲</span>, threshold: 95, thresholdLabel: '>= 95%', description: 'Near-perfect yearly progressive overload turns the arrows white-hot platinum.' }
    ]
  },
  // Nutrition configs for each time horizon - Temple theme
  nutritionWeek: {
    title: 'Weekly Foundation',
    description: 'Your weekly nutrition builds the foundation. Score is based on hitting calorie targets (within ±10%) and protein goals.',
    valueLabel: 'Week Score',
    formatValue: formatBadgePercentage,
    getCurrentValue: ({ nutritionCompliance }) => nutritionCompliance?.week?.complianceRate || 0,
    levels: [
      { label: 'Rubble', icon: <span className="temple-foundation-icon rubble">🪨</span>, threshold: 0, thresholdLabel: '< 30%', description: 'Scattered stones. Log meals and hit targets to lay the foundation.' },
      { label: 'Rough Stone', icon: <span className="temple-foundation-icon rough">░</span>, threshold: 30, thresholdLabel: '>= 30%', description: 'Gathering materials. The base is starting to form.' },
      { label: 'Laid Stone', icon: <span className="temple-foundation-icon laid">▒</span>, threshold: 50, thresholdLabel: '>= 50%', description: 'The foundation is taking shape. Keep building.' },
      { label: 'Solid Stone', icon: <span className="temple-foundation-icon solid">▓</span>, threshold: 70, thresholdLabel: '>= 70%', description: 'Strong foundation forming. Your body has what it needs.' },
      { label: 'Black Granite', icon: <span className="temple-foundation-icon black-granite">█</span>, threshold: 90, thresholdLabel: '>= 90%', description: 'A black-granite plinth anchors the monument. This week’s nutrition is unshakeable.' }
    ]
  },
  nutritionMonth: {
    title: 'Monthly Pillars',
    description: 'Monthly nutrition raises the pillars. These columns support everything above.',
    valueLabel: 'Month Score',
    formatValue: formatBadgePercentage,
    getCurrentValue: ({ nutritionCompliance }) => nutritionCompliance?.month?.complianceRate || 0,
    levels: [
      { label: 'Bare Posts', icon: <span className="temple-pillar-icon bare">|</span>, threshold: 0, thresholdLabel: '< 30%', description: 'The pillars haven\'t risen yet. Build your monthly nutrition.' },
      { label: 'Wooden Posts', icon: <span className="temple-pillar-icon wood">‖</span>, threshold: 30, thresholdLabel: '>= 30%', description: 'Wooden supports rise. Temporary, but functional.' },
      { label: 'Stone Columns', icon: <span className="temple-pillar-icon stone">𐌠𐌠</span>, threshold: 50, thresholdLabel: '>= 50%', description: 'Stone pillars take form. The structure is growing.' },
      { label: 'Marble Pillars', icon: <span className="temple-pillar-icon marble">🏛️</span>, threshold: 70, thresholdLabel: '>= 70%', description: 'Elegant marble columns rise. Refined and powerful.' },
      { label: 'Forged Steel Columns', icon: <span className="temple-pillar-icon forged-steel">┃┃</span>, threshold: 90, thresholdLabel: '>= 90%', description: 'Forged steel columns lock into the granite base—disciplined, load-bearing, and built to last.' }
    ]
  },
  nutritionYear: {
    title: 'Yearly Roof',
    description: 'The yearly roof crowns your temple. Each level represents roughly another month of hitting your nutrition targets.',
    valueLabel: 'Year Score',
    formatValue: formatBadgePercentage,
    getCurrentValue: ({ nutritionCompliance }) => nutritionCompliance?.year?.complianceRate || 0,
    levels: [
      { label: 'Open Sky', icon: <span className="temple-roof-icon open">○</span>, threshold: 0, thresholdLabel: '< 8.0%', description: 'Open to the sky. A year of good nutrition will crown your temple, one month at a time.' },
      { label: 'Roof Frame', icon: <span className="temple-roof-icon first-rafters">◠</span>, threshold: 8, thresholdLabel: '>= 8.0%', description: 'Rafters and beams are set. One month down — the framework is rising.' },
      { label: 'Partial Thatch', icon: <span className="temple-roof-icon partial-thatch">◠</span>, threshold: 17, thresholdLabel: '>= 17.0%', description: 'Thatch begins to cover the beams. Two months of nutrition logged — progress is visible.' },
      { label: 'Thatch Roof', icon: <span className="temple-roof-icon thatch">⌂</span>, threshold: 25, thresholdLabel: '>= 25.0%', description: 'A thatched roof provides shelter. Three months of effort — basic but functional.' },
      { label: 'Shingled Roof', icon: <span className="temple-roof-icon wood-complete">⌂</span>, threshold: 33, thresholdLabel: '>= 33.0%', description: 'Shingled wood covers the structure. Four months in — protection from the elements.' },
      { label: 'Stone Tiles', icon: <span className="temple-roof-icon stone-tiles">⌂</span>, threshold: 42, thresholdLabel: '>= 42.0%', description: 'Stone tiles replace the wood. Five months of building — a real roof is taking form.' },
      { label: 'Heavy Stone', icon: <span className="temple-roof-icon heavy-stone">⛩️</span>, threshold: 50, thresholdLabel: '>= 50.0%', description: 'Heavy stone covers the frame. Six months — halfway through the year, the structure is solid.' },
      { label: 'Stone Roof', icon: <span className="temple-roof-icon stone-sealed">⛩️</span>, threshold: 58, thresholdLabel: '>= 58.0%', description: 'Sealed stone tiles lock into place. Seven months — the roof is weatherproof and strong.' },
      { label: 'Marble Roof', icon: <span className="temple-roof-icon marble-slab">⛩️</span>, threshold: 67, thresholdLabel: '>= 67.0%', description: 'Fitted marble slabs crown the roof. Eight months of focus — elegant and enduring.' },
      { label: 'Marble Dome', icon: <span className="temple-roof-icon dome-rising">🏛️</span>, threshold: 75, thresholdLabel: '>= 75.0%', description: 'A polished marble dome rises. Nine months strong — the temple is becoming a landmark.' },
      { label: 'Grand Dome', icon: <span className="temple-roof-icon grand-dome">🏛️</span>, threshold: 83, thresholdLabel: '>= 83.0%', description: 'A grand marble dome crowns the structure. Ten months of discipline — your temple commands respect.' },
      { label: 'Apex Monument', icon: <span className="temple-roof-icon apex-monument">🏛️</span>, threshold: 92, thresholdLabel: '>= 92.0%', description: 'Black stone and forged gold crown eleven months of disciplined nutrition.' },
      { label: 'Platinum Monument', icon: <span className="temple-roof-icon platinum-monument">🏛️</span>, threshold: 100, thresholdLabel: '100.0%', description: 'Twelve perfect months. The monument receives a final platinum edge—rare, severe, and earned.' }
    ]
  }
};

const ComplianceModal = ({ modalService, rates, sevenYearStars = 0, goldYearEquivalents = 0, volumePRs = null, nutritionCompliance = null, getShimmerClass, getStars, getCentralShape, getBulletChevrons, getBulletStars, getBulletCentralShape, getFlameClass }) => {
  const openLevelModal = (windowKey) => {
    const config = complianceLevelConfigs[windowKey];
    if (!config) return;
    const modalContext = { rates, sevenYearStars, goldYearEquivalents, volumePRs, nutritionCompliance };
    const currentValue = typeof config.getCurrentValue === 'function'
      ? config.getCurrentValue(modalContext)
      : rates[windowKey] ?? 0;
    const secondaryValue = typeof config.getSecondaryValue === 'function'
      ? config.getSecondaryValue(modalContext)
      : undefined;
    modalService.open(BadgeLevelModal, {
      title: config.title,
      description: typeof config.description === 'function' ? config.description(modalContext) : config.description,
      currentValue,
      levels: config.levels,
      valueLabel: config.valueLabel,
      formatValue: config.formatValue ? (v) => config.formatValue(v, modalContext) : undefined,
      secondaryValue,
      secondaryValueLabel: config.secondaryValueLabel,
      formatSecondaryValue: config.formatSecondaryValue,
      extraInfo: config.extraInfo
    });
  };

  const handleRowKeyDown = (event, windowKey) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLevelModal(windowKey);
    }
  };

  const hasGoldYearProgress = Number.isFinite(goldYearEquivalents) && goldYearEquivalents >= 1;

  return (
    <div className="compliance-modal">
      {/* Large badge display at the top */}
      <div className="compliance-badge-large">
        <Badge 
          rates={rates}
          starCount={sevenYearStars}
          size="large"
          getShimmerClass={getShimmerClass}
          getStars={getStars}
          getCentralShape={getCentralShape}
          getFlameClass={getFlameClass}
          volumePRs={volumePRs}
          nutritionCompliance={nutritionCompliance}
        />
      </div>
      
      <div className="compliance-details">
        <div className="compliance-grid">
          {/* Discipline card */}
          <div className="compliance-card">
            <div className="compliance-card-header">
              <span className="compliance-card-label">Consistency</span>
            </div>
            <div className="compliance-card-values">
              <span 
                className="compliance-clickable-value"
                onClick={() => openLevelModal('week')}
                onKeyDown={(event) => handleRowKeyDown(event, 'week')}
                role="button"
                tabIndex={0}
                aria-label={`View weekly flame levels, current rate ${formatBadgePercentage(rates.week)}`}
              >
                <span className="compliance-value-icon">
                  <span className={`compliance-mini-flame ${roundedBadgePercentageValue(rates.week) >= 100 ? 'flame-platinum' : roundedBadgePercentageValue(rates.week) >= 90 ? 'flame-white' : roundedBadgePercentageValue(rates.week) >= 60 ? 'flame-orange' : roundedBadgePercentageValue(rates.week) >= 30 ? 'flame-red' : 'flame-none'}`} />
                </span>
                <span className="compliance-value-number">{formatBadgePercentage(rates.week)}</span>
                <span className="compliance-value-label">week</span>
              </span>
              <span 
                className="compliance-clickable-value"
                onClick={() => openLevelModal('month')}
                onKeyDown={(event) => handleRowKeyDown(event, 'month')}
                role="button"
                tabIndex={0}
                aria-label={`View monthly shimmer levels, current rate ${formatBadgePercentage(rates.month)}`}
              >
                <span className="compliance-value-icon">
                  <span className={`compliance-mini-shimmer ${roundedBadgePercentageValue(rates.month) >= 100 ? 'shimmer-obsidian' : roundedBadgePercentageValue(rates.month) >= 90 ? 'shimmer-gold' : roundedBadgePercentageValue(rates.month) >= 60 ? 'shimmer-silver' : roundedBadgePercentageValue(rates.month) >= 30 ? 'shimmer-bronze' : 'shimmer-none'}`} />
                </span>
                <span className="compliance-value-number">{formatBadgePercentage(rates.month)}</span>
                <span className="compliance-value-label">month</span>
              </span>
              <span 
                className="compliance-clickable-value"
                onClick={() => openLevelModal('year')}
                onKeyDown={(event) => handleRowKeyDown(event, 'year')}
                role="button"
                tabIndex={0}
                aria-label={`View yearly totem levels, current rate ${formatBadgePercentage(rates.year)}`}
              >
                <span className="compliance-value-icon compliance-value-icon-emoji">
                  {getBulletCentralShape(roundedBadgePercentageValue(rates.year))}
                </span>
                <span className="compliance-value-number">{formatBadgePercentage(rates.year)}</span>
                <span className="compliance-value-label">year</span>
              </span>
            </div>
          </div>

          {/* Growth card */}
          {volumePRs && (
            <div className="compliance-card">
              <div className="compliance-card-header">
              <span className="compliance-card-label">Intensity</span>
              </div>
              <div className="compliance-card-values">
                <span 
                  className="compliance-clickable-value"
                  onClick={() => openLevelModal('growthWeek')}
                  onKeyDown={(event) => handleRowKeyDown(event, 'growthWeek')}
                  role="button"
                  tabIndex={0}
                  aria-label={`View weekly growth animation speed levels, current rate ${formatBadgePercentage(volumePRs?.week?.prRate)}`}
                >
                  <span className="compliance-value-icon">
                    {(() => {
                      const weekRate = roundedBadgePercentageValue(volumePRs?.week?.prRate);
                      const count = weekRate >= 95 ? 3 : weekRate >= 90 ? 3 : weekRate >= 60 ? 2 : weekRate >= 30 ? 1 : 1;
                      const speed = weekRate >= 95 ? '0.8s' : weekRate >= 90 ? '1s' : weekRate >= 60 ? '1.3s' : weekRate >= 30 ? '1.8s' : '2.5s';
                      return (
                        <span className="compliance-mini-arrow-stack" style={{ '--arrow-speed': speed, opacity: weekRate < 30 ? 0.3 : 1 }}>
                          {Array.from({ length: count }).map((_, i) => (
                            <span key={i} className="compliance-mini-arrow-animated" style={{ animationDelay: `${i * 0.2}s` }}>▲</span>
                          ))}
                        </span>
                      );
                    })()}
                  </span>
                  <span className="compliance-value-number">{formatBadgePercentage(volumePRs?.week?.prRate)}</span>
                  <span className="compliance-value-label">week</span>
                </span>
                <span 
                  className="compliance-clickable-value"
                  onClick={() => openLevelModal('growthMonth')}
                  onKeyDown={(event) => handleRowKeyDown(event, 'growthMonth')}
                  role="button"
                  tabIndex={0}
                  aria-label={`View monthly growth arrow size levels, current rate ${formatBadgePercentage(volumePRs?.month?.prRate)}`}
                >
                  <span className="compliance-value-icon">
                    {(() => {
                      const monthRate = roundedBadgePercentageValue(volumePRs?.month?.prRate);
                      const size = monthRate >= 95 ? '14px' : monthRate >= 90 ? '12px' : monthRate >= 60 ? '10px' : monthRate >= 30 ? '8px' : '6px';
                      const symbol = monthRate >= 90 ? '▲' : monthRate >= 30 ? '△' : '˄';
                      return <span style={{ fontSize: size, opacity: monthRate < 30 ? 0.4 : 1 }}>{symbol}</span>;
                    })()}
                  </span>
                  <span className="compliance-value-number">{formatBadgePercentage(volumePRs?.month?.prRate)}</span>
                  <span className="compliance-value-label">month</span>
                </span>
                <span 
                  className="compliance-clickable-value"
                  onClick={() => openLevelModal('growthYear')}
                  onKeyDown={(event) => handleRowKeyDown(event, 'growthYear')}
                  role="button"
                  tabIndex={0}
                  aria-label={`View yearly growth arrow color levels, current rate ${formatBadgePercentage(volumePRs?.year?.prRate)}`}
                >
                  <span className="compliance-value-icon">
                    {(() => {
                      const yearRate = roundedBadgePercentageValue(volumePRs?.year?.prRate);
                      const color = yearRate >= 95 ? '#f8fafc' : yearRate >= 90 ? '#fbbf24' : yearRate >= 60 ? '#a3e635' : yearRate >= 30 ? '#60a5fa' : 'rgba(255,255,255,0.3)';
                      return <span className="compliance-mini-arrow" style={{ color, textShadow: yearRate >= 30 ? `0 0 4px ${color}` : 'none' }}>▲</span>;
                    })()}
                  </span>
                  <span className="compliance-value-number">{formatBadgePercentage(volumePRs?.year?.prRate)}</span>
                  <span className="compliance-value-label">year</span>
                </span>
              </div>
            </div>
          )}

          {/* Nutrition card - Temple Theme */}
          {nutritionCompliance && (
            <div className="compliance-card">
              <div className="compliance-card-header">
                <span className="compliance-card-label">Nutrition</span>
              </div>
              <div className="compliance-card-values">
                <span 
                  className="compliance-clickable-value"
                  onClick={() => openLevelModal('nutritionWeek')}
                  onKeyDown={(event) => handleRowKeyDown(event, 'nutritionWeek')}
                  role="button"
                  tabIndex={0}
                  aria-label={`View weekly foundation levels, current score ${formatBadgePercentage(nutritionCompliance?.week?.complianceRate)}`}
                >
                  <span className="compliance-value-icon">
                    {(() => {
                      const rate = roundedBadgePercentageValue(nutritionCompliance?.week?.complianceRate);
                      if (rate >= 90) return <span className="temple-mini-foundation black-granite">█</span>;
                      if (rate >= 70) return <span className="temple-mini-foundation solid">▓</span>;
                      if (rate >= 50) return <span className="temple-mini-foundation laid">▒</span>;
                      if (rate >= 30) return <span className="temple-mini-foundation rough">░</span>;
                      return <span className="temple-mini-foundation rubble">·</span>;
                    })()}
                  </span>
                  <span className="compliance-value-number">{formatBadgePercentage(nutritionCompliance?.week?.complianceRate)}</span>
                  <span className="compliance-value-label">foundation</span>
                </span>
                <span 
                  className="compliance-clickable-value"
                  onClick={() => openLevelModal('nutritionMonth')}
                  onKeyDown={(event) => handleRowKeyDown(event, 'nutritionMonth')}
                  role="button"
                  tabIndex={0}
                  aria-label={`View monthly pillar levels, current score ${formatBadgePercentage(nutritionCompliance?.month?.complianceRate)}`}
                >
                  <span className="compliance-value-icon">
                    {(() => {
                      const rate = roundedBadgePercentageValue(nutritionCompliance?.month?.complianceRate);
                      if (rate >= 90) return <span className="temple-mini-pillars forged-steel">┃┃</span>;
                      if (rate >= 70) return <span className="temple-mini-pillars marble">║║</span>;
                      if (rate >= 50) return <span className="temple-mini-pillars stone">┃┃</span>;
                      if (rate >= 30) return <span className="temple-mini-pillars wood">||</span>;
                      return <span className="temple-mini-pillars bare">¦¦</span>;
                    })()}
                  </span>
                  <span className="compliance-value-number">{formatBadgePercentage(nutritionCompliance?.month?.complianceRate)}</span>
                  <span className="compliance-value-label">pillars</span>
                </span>
                <span 
                  className="compliance-clickable-value"
                  onClick={() => openLevelModal('nutritionYear')}
                  onKeyDown={(event) => handleRowKeyDown(event, 'nutritionYear')}
                  role="button"
                  tabIndex={0}
                  aria-label={`View yearly roof levels, current score ${formatBadgePercentage(nutritionCompliance?.year?.complianceRate)}`}
                >
                  <span className="compliance-value-icon">
                    {(() => {
                      const rate = roundedBadgePercentageValue(nutritionCompliance?.year?.complianceRate);
                      if (rate >= 100) return <span className="temple-mini-roof platinum-monument">🏛️</span>;
                      if (rate >= 92) return <span className="temple-mini-roof apex-monument">🏛️</span>;
                      if (rate >= 83) return <span className="temple-mini-roof grand-dome">🏛️</span>;
                      if (rate >= 75) return <span className="temple-mini-roof dome-rising">🏛️</span>;
                      if (rate >= 67) return <span className="temple-mini-roof marble-slab">⛩️</span>;
                      if (rate >= 58) return <span className="temple-mini-roof stone-sealed">⛩️</span>;
                      if (rate >= 50) return <span className="temple-mini-roof heavy-stone">⛩️</span>;
                      if (rate >= 42) return <span className="temple-mini-roof stone-tiles">⌂</span>;
                      if (rate >= 33) return <span className="temple-mini-roof wood-complete">⌂</span>;
                      if (rate >= 25) return <span className="temple-mini-roof thatch">⌂</span>;
                      if (rate >= 17) return <span className="temple-mini-roof partial-thatch">◠</span>;
                      if (rate >= 8) return <span className="temple-mini-roof first-rafters">◠</span>;
                      return <span className="temple-mini-roof open">○</span>;
                    })()}
                  </span>
                  <span className="compliance-value-number">{formatBadgePercentage(nutritionCompliance?.year?.complianceRate)}</span>
                  <span className="compliance-value-label">roof</span>
                </span>
              </div>
            </div>
          )}

          {/* Starfield card (only if gold year progress) */}
          {hasGoldYearProgress && (
            <div 
              className="compliance-card compliance-card-clickable"
              onClick={() => openLevelModal('sevenYears')}
              onKeyDown={(event) => handleRowKeyDown(event, 'sevenYears')}
              role="button"
              tabIndex={0}
              aria-label={`View starfield badge detail levels, ${sevenYearStars} ${sevenYearStars === 1 ? 'star' : 'stars'}, ${goldYearEquivalents.toFixed(1)} gold years`}
            >
              <div className="compliance-card-header">
                <span className="compliance-card-label">Starfield</span>
              </div>
              <div className="compliance-card-single-value">
                <span className="compliance-value-icon compliance-value-icon-emoji">{getBulletStars(sevenYearStars)}</span>
                <span className="compliance-value-number">{sevenYearStars}★</span>
                <span className="compliance-value-label">{goldYearEquivalents.toFixed(1)} gold years</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceModal;